import { find } from "ramda";
import { type FC, type ReactNode, useEffect, useState } from "react";
import { Item, Section } from "react-stately";
import { MenuOptionItem, SelectList } from "widgets";

import { useError } from "@/app-utils/graphql/useError.ts";
import { ProjectColoredBadge } from "@/modules/projects/components/ProjectColoredWidgets.tsx";
import { type ProjectsFetchOptions, useFetchProjects } from "@/modules/projects/hooks/useFetchProjects";

export interface ProjectsListBox extends ProjectsFetchOptions {
  withDescription?: boolean;
  loadingFallback?: ReactNode;
}

export const ProjectsListBox: FC<ProjectsListBox> = (props) => {
  const { projectsList, projectsTree, error, isLoading, defaultSelectedProject } = useFetchProjects({
    includeStagedProjects: !props.includeStagedProjects,
    defaultSelection: props.defaultSelection,
  });

  void useError(error);

  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  useEffect(() => {
    // sync (props.initialSelectedProjectId)
    if (props.initialSelectedProjectId != null) {
      setSelectedKey(String(props.initialSelectedProjectId));
    }
  }, [props.initialSelectedProjectId]);

  useEffect(() => {
    // async (defaultSelectedKey)
    // We don't want to reassign initialSelectedProjectId if defaultSelectedKey changes in the future
    const selection = defaultSelectedProject ?? projectsTree?.[0]?.items?.[0];
    if (props.initialSelectedProjectId == null && selection != null) {
      setSelectedKey(String(selection.id));
      props.onSelectionChange?.(selection);
    }
  }, [props.initialSelectedProjectId, defaultSelectedProject, props.onSelectionChange, projectsTree]);

  return isLoading ? (
    props.loadingFallback
  ) : (
    <SelectList
      noBorder
      items={projectsTree}
      selectedKeys={selectedKey != null ? new Set([String(selectedKey)]) : undefined}
      onSelectionChange={(keys) => {
        if (keys instanceof Set) {
          const key = keys.values().next().value;
          const project = find((project) => project.id === Number(key), projectsList ?? []);
          props.onSelectionChange?.(project);
          setSelectedKey(String(key));
        }
      }}
      disallowEmptySelection
      selectionMode="single"
    >
      {(section) => (
        <Section items={section.items} key={section.name} title={section.name}>
          {(item) => (
            <Item key={String(item.id)}>
              <MenuOptionItem
                title={item.name}
                description={props.withDescription ? item.description : undefined}
                icon={item.color ? <ProjectColoredBadge project={item} noLink size="menu" /> : "folder"}
                useHover
              />
            </Item>
          )}
        </Section>
      )}
    </SelectList>
  );
};
